import { BaseAPI } from "./BaseAPI";

import type { APIConf } from "./BaseAPI";
import type { SmartlistType } from "./MediaAPI.types";
import type {
  CreateDisplayParams,
  CreateDisplayResponse,
  CreateDisplayTranslationParams,
  CreateDisplayTranslationResponse,
  CreateSeasonParams,
  CreateTranslationParams,
  CreateTranslationResponse,
  DeleteDisplayParams,
  DeletePictureParams,
  DeleteSeasonParams,
  DeleteSmartlistPictureTagResponse,
  GetDisplayListParams,
  GetDisplayListResponse,
  GetDisplayParams,
  GetDisplayResponse,
  GetSeasonsParams,
  GetSmartlistIndexListResponse,
  GetSmartlistListResponse,
  GetSmartlistResponse,
  SmartlistSeason,
  SmartlistToCreate,
  UpdateDisplayParams,
  UpdatedSmartlistPayload,
  UpdateSmartlistPictureTagParams,
  UpdateSmartlistPictureTagResponse,
  UploadPictureParams,
  UploadPictureResponse,
} from "./SmartlistAPI.types";

class SmartlistAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "smartlist";
    this.version = "v3";
  }

  async getSmartlistList({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetSmartlistListResponse> {
    return this.axios
      .post<GetSmartlistListResponse>(
        `${this.routeBase}/admin/smartlists`,
        {
          nested: {
            smartlist_items: {},
            countries: {},
            translations: {},
            custom_params: {},
            comments: {},
          },
          page: 1,
          per_page: 100000,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getSingleSmartlist({
    smartlistId,
    projectSlug,
  }: {
    smartlistId: string | number;
    projectSlug: string;
  }): Promise<GetSmartlistResponse> {
    return this.axios
      .get<GetSmartlistResponse>(
        `/api/smartlist/v4/admin/smartlist/${smartlistId}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async createSmartlist({
    data,
    projectSlug,
  }: {
    data: SmartlistToCreate;
    projectSlug: string;
  }): Promise<{ uuid: string }> {
    return this.axios
      .put<{ uuid: string }>(`${this.routeBase}/admin/smartlists`, data, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateSmartlist({
    uuid,
    data,
    projectSlug,
  }: {
    uuid: string | number;
    data: UpdatedSmartlistPayload;
    projectSlug: string;
  }): Promise<GetSmartlistResponse> {
    return this.axios
      .put<GetSmartlistResponse>(
        `${this.apiBase}/${this.service}/v4/admin/smartlists/${uuid}`,
        data,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deleteSmartlist({
    uuid,
    projectSlug,
  }: {
    uuid: string | number;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/smartlists/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async copySmartlistToProject({
    projectUUID,
    smartlistUUID,
    projectSlug,
  }: {
    projectUUID: string;
    smartlistUUID: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .post(
        `${this.routeBase}/admin/smartlists/copy_smartlist`,
        {
          smartlist_uuid: smartlistUUID,
          to_sfp_uuid: projectUUID,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async uploadPicture({
    smartlistId,
    pictureFile,
    pictureFormat,
    translationId,
    projectSlug,
    isDisplay,
  }: UploadPictureParams): Promise<UploadPictureResponse> {
    const formData = new FormData();
    formData.append("picture", pictureFile);

    const smartlistSubType = isDisplay ? "displays" : "smartlists";

    return this.axios
      .post<UploadPictureResponse>(
        `${this.routeBase}/admin/${smartlistSubType}/${smartlistId}/picture`,
        formData,
        {
          params: {
            translation_uuid: translationId,
            picture_field: pictureFormat,
          },
          headers: {
            "Content-Type": "multipart/form-data",
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async deletePicture({
    smartlistId,
    translationId,
    projectSlug,
    isDisplay,
  }: DeletePictureParams): Promise<void> {
    const smartlistSubType = isDisplay ? "displays" : "smartlists";
    await this.axios.delete(
      `${this.routeBase}/admin/${smartlistSubType}/${smartlistId}/picture`,
      {
        params: {
          translation_uuid: translationId,
        },
        headers: {
          "xx-ok-project": projectSlug,
        },
      },
    );
  }

  async createSeason({
    serieId,
    season,
    projectSlug,
  }: CreateSeasonParams): Promise<SmartlistSeason> {
    return this.axios
      .post<SmartlistSeason>(
        `${this.apiBase}/${this.service}/v4/admin/smartlist/${serieId}/season`,
        season,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getSeasonList({
    serieId,
    projectSlug,
  }: GetSeasonsParams): Promise<SmartlistSeason[]> {
    return this.axios
      .get<SmartlistSeason[]>(
        `${this.apiBase}/${this.service}/v4/admin/serie/${serieId}/seasons`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deleteSeason({
    seasonId,
    serieId,
    projectSlug,
  }: DeleteSeasonParams): Promise<unknown> {
    return this.axios
      .delete(
        `${this.apiBase}/${this.service}/v4/admin/smartlist/${seasonId}`,
        {
          data: {
            serie_uuid: serieId,
          },
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deletePictureTag({
    tagId,
    projectSlug,
  }: {
    tagId: string;
    projectSlug: string;
  }): Promise<DeleteSmartlistPictureTagResponse> {
    return this.axios
      .delete<DeleteSmartlistPictureTagResponse>(
        `${this.routeBase}/admin/smartlists/tags/${tagId}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async updatePictureTag({
    tagId,
    tagValue,
    translationId,
    ratioId,
    projectSlug,
  }: UpdateSmartlistPictureTagParams): Promise<UpdateSmartlistPictureTagResponse> {
    return this.axios
      .put<UpdateSmartlistPictureTagResponse>(
        `${this.routeBase}/admin/smartlists/tags/${tagId}`,
        {
          picture_field: ratioId,
          translation_uuid: translationId,
          label: tagValue,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getDisplayList({
    projectSlug,
  }: GetDisplayListParams): Promise<GetDisplayListResponse> {
    return this.axios
      .get<GetDisplayListResponse>(`${this.routeBase}/admin/displays/`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getDisplay({
    uuid,
    projectSlug,
  }: GetDisplayParams): Promise<GetDisplayResponse> {
    return this.axios
      .get<GetDisplayResponse>(`${this.routeBase}/admin/displays/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateDisplay({
    uuid,
    data,
    projectSlug,
  }: UpdateDisplayParams): Promise<GetDisplayResponse> {
    return this.axios
      .put<GetDisplayResponse>(
        `${this.routeBase}/admin/displays/${uuid}`,
        data,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async createDisplay({
    data,
    projectSlug,
  }: CreateDisplayParams): Promise<CreateDisplayResponse> {
    return this.axios
      .post<CreateDisplayResponse>(`${this.routeBase}/admin/displays`, data, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async deleteDisplay({
    uuid,
    projectSlug,
  }: DeleteDisplayParams): Promise<void> {
    await this.axios.delete(`${this.routeBase}/admin/displays/${uuid}`, {
      headers: { "xx-ok-project": projectSlug },
    });
  }

  async getSmartlistIndexList({
    projectSlug,
    smartlistType,
  }: {
    projectSlug: string;
    smartlistType: SmartlistType | SmartlistType[];
  }): Promise<GetSmartlistIndexListResponse> {
    const smartlistTypesString = Array.isArray(smartlistType)
      ? smartlistType.join(",")
      : smartlistType;
    return this.axios
      .get<GetSmartlistIndexListResponse>(
        `${this.apiBase}/${this.service}/v4/admin/index`,
        {
          headers: { "xx-ok-project": projectSlug },
          params: {
            type: smartlistTypesString,
          },
        },
      )
      .then((response) => response.data);
  }

  async createDisplayTranslation({
    displayId,
    languageUuid,
    projectSlug,
  }: CreateDisplayTranslationParams): Promise<CreateDisplayTranslationResponse> {
    return this.axios
      .post<CreateDisplayTranslationResponse>(
        `${this.routeBase}/admin/displays/${displayId}/translation`,
        { language_uuid: languageUuid },
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  async createTranslation({
    smartlistId,
    languageUuid,
    projectSlug,
  }: CreateTranslationParams): Promise<CreateTranslationResponse> {
    return this.axios
      .post<CreateTranslationResponse>(
        `${this.apiBase}/${this.service}/v4/admin/smartlists/${smartlistId}/translation`,
        {
          language_uuid: languageUuid,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }
}

const SmartlistAPI = new SmartlistAPIClass({
  token: localStorage.token,
});

export default SmartlistAPI;
